import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { requestNotificationPermission, isPushSubscribed, addTags, processPendingExternalUserId } from "../lib/onesignal";
import TheoOrb from "./TheoOrb";
import { 
  fetchAllUserData, 
  fetchOnboardingData,
  fetchCycleData,
  fetchTodayCheckin,
  fetchTodayHabits,
  saveCheckin,
  saveHabits,
  fetchUserName,
  getVerifiedUserId
} from "../lib/userDataService";
import { detectHormonePattern, normalizeHormones } from "../lib/intelligenceService";

const quizQuestions = [
  { id: "q1", text: "Are your periods irregular?" },
  { id: "q2", text: "Do you experience persistent acne?" },
  { id: "q3", text: "Have you noticed facial or body hair growth?" },
  { id: "q4", text: "Do you struggle with unexplained weight gain?" },
  { id: "q5", text: "Has a doctor mentioned insulin resistance?" },
];

// FEATURE 5: Daily Check-In Config
const checkinItems = [
  { key: 'mood', label: 'Mood', emoji: '😊' },
  { key: 'fatigue', label: 'Fatigue', emoji: '😴' },
  { key: 'acne', label: 'Acne', emoji: '🌿' },
  { key: 'bloating', label: 'Bloating', emoji: '🫧' },
  { key: 'cravings', label: 'Cravings', emoji: '🍫' },
  { key: 'energy', label: 'Energy', emoji: '⚡' },
];
const checkinLevels = ['None', 'Mild', 'Moderate', 'Severe'];

// Habit tracker items
const habitItems = [
  { key: 'water', label: 'Drink 8 glasses of water', emoji: '💧' },
  { key: 'exercise', label: '30 min exercise', emoji: '🏃‍♀️' },
  { key: 'sleep', label: '7+ hours sleep', emoji: '😴' },
  { key: 'supplements', label: 'Take supplements', emoji: '💊' },
];

const symptomLabels = {
  acne: "Acne",
  hair_loss: "Hair Loss",
  mood: "Mood",
  fatigue: "Fatigue",
  sleep_quality: "Sleep Quality",
  weight_change: "Weight Change",
};
const severityLabels = ["None", "Mild", "Moderate", "Severe"];

const computeFlagCount = (r) => {
  // Route through the canonical normalizer so the legacy `testosteone`
  // column is handled in ONE place (intelligenceService.normalizeHormones).
  const n = normalizeHormones(r) || {};
  let c = 0;
  if (n.lh != null && (n.lh < 2 || n.lh > 10)) c++;
  if (n.fsh != null && (n.fsh < 3 || n.fsh > 10)) c++;
  if (n.insulin != null && n.insulin >= 8) c++;
  if (n.testosterone != null && (n.testosterone < 15 || n.testosterone > 70)) c++;
  if (n.amh != null && (n.amh < 1 || n.amh > 3.5)) c++;
  return c;
};

export default function HomeScreen({ userId, onNavigate }) {
  const [userName, setUserName] = useState("there");
  const [hormoneInsight, setHormoneInsight] = useState(null);
  const [todayLog, setTodayLog] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [quizStarted, setQuizStarted] = useState(false);
  
  // FEATURE 5: Daily Check-In State
  const [showCheckinModal, setShowCheckinModal] = useState(false);
  const [checkinScores, setCheckinScores] = useState({
    mood: null, fatigue: null, acne: null, bloating: null, cravings: null, energy: null
  });
  const [checkinComplete, setCheckinComplete] = useState(false);

  // Context state for improved home screen
  const [cyclePhase, setCyclePhase] = useState(null);
  const [cycleDay, setCycleDay] = useState(null);
  const [cycleLengthAvg, setCycleLengthAvg] = useState(null);
  const [cyclesTracked, setCyclesTracked] = useState(0);
  const [lastReportStatus, setLastReportStatus] = useState(null);
  const [weeklyCheckinCount, setWeeklyCheckinCount] = useState(0);
  const [hormonePatternName, setHormonePatternName] = useState(null);
  const [hormonePattern, setHormonePattern] = useState(null);  // derived via intelligenceService.detectHormonePattern
  const [symptomsLoggedCount, setSymptomsLoggedCount] = useState(0);
  
  // Habit tracker state (will be loaded from user-specific storage in useEffect)
  const [habits, setHabits] = useState({ water: false, exercise: false, sleep: false, supplements: false });
  
  // Animation state
  const [mounted, setMounted] = useState(false);
  
  // PCOS Pattern Score state
  const [pcosPatternScore, setPcosPatternScore] = useState(null);
  const [pcosSignals, setPcosSignals] = useState([]);
  
  // Onboarding data for personalization
  const [onboardingData, setOnboardingData] = useState(null);
  
  // PCOS Health Snapshot state
  const [healthSnapshot, setHealthSnapshot] = useState({
    hasAnyData: false,
    overallStatus: null, // 'low', 'moderate', 'high'
    riskScore: 0,
    signals: [],
    recommendation: '',
    dataAvailable: {
      cycle: false,
      bleeding: false,
      symptoms: false,
      habits: false,
      hormones: false
    }
  });
  
  // Push notification state
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [notificationDismissed, setNotificationDismissed] = useState(() => {
    return localStorage.getItem('pcosNotificationPromptDismissed') === 'true';
  });
  
  // Loading state — prevents rendering empty UI before data arrives
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    setMounted(true);
    
    const load = async () => {
      // ── verify auth is ready before any DB call ──
      const verifiedId = await getVerifiedUserId();
      const uid = verifiedId || userId;
      console.log('[HomeScreen] Loading data for user:', uid);
      
      // Fetch onboarding data from service (Supabase first, then fallback)
      const onboarding = await fetchOnboardingData(uid);
      if (onboarding) {
        setOnboardingData(onboarding);
        console.log('[HomeScreen] STATE AFTER SET onboardingData:', onboarding);
      }
      
      // Setup OneSignal with user info
      setupOneSignal();
      
      // Fetch user profile name using the service (avoids concurrent Supabase calls)
      const profileName = await fetchUserName(uid);
      if (profileName) {
        const firstName = profileName.split(' ')[0];
        setUserName(firstName || "there");
        console.log('[HomeScreen] STATE AFTER SET userName:', firstName);
      }

      // Fetch hormone report from Supabase
      const { data: report, error: hormoneErr } = await supabase
        .from("hormone_reports")
        .select("*")
        .eq("user_id", uid)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (hormoneErr) {
        console.log('[HomeScreen] Hormone fetch error:', hormoneErr.message);
      }
      console.log('[HomeScreen] Fetched hormone_reports:', report);
      
      if (report) {
        const flagCount = computeFlagCount(report);
        setHormoneInsight(flagCount);
        
        const reportDate = new Date(report.created_at);
        const daysSince = Math.floor((new Date() - reportDate) / (1000 * 60 * 60 * 24));
        if (daysSince === 0) setLastReportStatus("today");
        else if (daysSince === 1) setLastReportStatus("yesterday");
        else if (daysSince <= 7) setLastReportStatus(`${daysSince} days ago`);
        else setLastReportStatus(`${Math.floor(daysSince / 7)} week${daysSince >= 14 ? 's' : ''} ago`);
      }

      // Fetch today's symptom log from Supabase
      const today = new Date().toISOString().split("T")[0];
      const { data: symptomLog } = await supabase
        .from("symptom_logs")
        .select("acne,hair_loss,mood,fatigue,sleep_quality,weight_change")
        .eq("user_id", uid)
        .eq("log_date", today)
        .maybeSingle();
      if (symptomLog) setTodayLog(symptomLog);

      // Fetch today's check-in from service
      const todayCheckin = await fetchTodayCheckin(uid);
      if (todayCheckin) setCheckinComplete(true);

      // Fetch today's habits
      const todayHabits = await fetchTodayHabits(uid);
      if (todayHabits) {
        setHabits({
          water: todayHabits.water || false,
          exercise: todayHabits.exercise || false,
          sleep: todayHabits.sleep || false,
          supplements: todayHabits.supplements || false
        });
      }

      // Calculate weekly check-in count from Supabase
      const now = new Date();
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - 7);
      
      const { data: weeklyCheckins } = await supabase
        .from("daily_checkins")
        .select("checkin_date")
        .eq("user_id", uid)
        .gte("checkin_date", weekStart.toISOString().split("T")[0])
        .lte("checkin_date", today);
      
      setWeeklyCheckinCount(weeklyCheckins?.length || 0);

      // Load hormone data for pattern analysis (via canonical normalizer)
      const normalized = normalizeHormones(report);
      const hormoneData = normalized ? {
        ...normalized,
        ratio: normalized.lh && normalized.fsh ? normalized.lh / normalized.fsh : null,
      } : null;
      
      if (hormoneData) {
        // Canonical pattern detection via central intelligence layer
        const pattern = detectHormonePattern(hormoneData);
        setHormonePattern(pattern);
        const lhFshRatio = hormoneData.ratio;
        if (lhFshRatio && lhFshRatio > 2) {
          setHormonePatternName("Elevated LH/FSH Pattern");
        }
      } else {
        setHormonePattern(null);
      }

      // Count symptoms logged this week from Supabase
      const { data: weeklySymptoms } = await supabase
        .from("symptom_logs")
        .select("log_date")
        .eq("user_id", uid)
        .gte("log_date", weekStart.toISOString().split("T")[0])
        .lte("log_date", today);
      
      setSymptomsLoggedCount(weeklySymptoms?.length || 0);

      // ── Fetch cycle data from SUPABASE (not localStorage) ──
      const cycleDataFromDb = await fetchCycleData(uid);
      console.log('[HomeScreen] Fetched cycle data from Supabase:', cycleDataFromDb);
      
      const activeCycleData = cycleDataFromDb;
      
      if (activeCycleData?.lastPeriodDate && activeCycleData?.cycleLength) {
        const lastPeriod = new Date(activeCycleData.lastPeriodDate);
        const daysSinceStart = Math.floor((now - lastPeriod) / (1000 * 60 * 60 * 24));
        const cycleLen = parseInt(activeCycleData.cycleLength) || 28;
        const currentDay = (daysSinceStart % cycleLen) + 1;
        setCycleDay(currentDay);
        console.log('[HomeScreen] STATE AFTER SET cycleDay:', currentDay);

        if (currentDay <= 5) setCyclePhase("Period");
        else if (currentDay <= 13) setCyclePhase("Follicular Phase");
        else if (currentDay <= 16) setCyclePhase("Ovulation");
        else setCyclePhase("Luteal Phase");
      } else if (activeCycleData?.cycleDay && activeCycleData?.phase && activeCycleData.phase !== 'unknown') {
        // Use stored day/phase if lastPeriodDate not set
        setCycleDay(activeCycleData.cycleDay);
        setCyclePhase(activeCycleData.phase);
        console.log('[HomeScreen] Using stored cycle day/phase:', activeCycleData.cycleDay, activeCycleData.phase);
      }

      // Cycle metadata for the header — only shown when we have both a
      // stored average AND at least one tracked cycle. Never hardcoded.
      if (activeCycleData?.cycleLength) {
        const len = parseInt(activeCycleData.cycleLength);
        if (len && len >= 15 && len <= 90) setCycleLengthAvg(len);
      }
      try {
        const rawHistory = localStorage.getItem(`period_history_${uid}`) || localStorage.getItem('period_history');
        if (rawHistory) {
          const hist = JSON.parse(rawHistory);
          if (Array.isArray(hist) && hist.length > 0) setCyclesTracked(hist.length);
        }
      } catch { /* localStorage may be unavailable */ }
      
      // Calculate PCOS Pattern Score
      let score = 0;
      const signals = [];
      let hasData = false;
      
      // ── Hormone-based scoring ──
      if (hormoneData) {
        hasData = true;
        console.log('[HomeScreen] Hormone data in score:', hormoneData);
        
        // LH/FSH ratio > 2 → elevated
        if (hormoneData.ratio && hormoneData.ratio > 2) {
          score += 20;
          signals.push("Elevated LH/FSH ratio");
        }
        
        // Testosterone > 70 ng/dL → high
        const testo = parseFloat(hormoneData.testosterone) || 0;
        if (testo > 70) {
          score += 15;
          signals.push("Elevated testosterone");
        }
        
        // Fasting insulin > 12 µIU/mL → insulin resistance
        const ins = parseFloat(hormoneData.insulin) || 0;
        if (ins > 12) {
          score += 15;
          signals.push("Elevated insulin");
        }
        
        // AMH > 3.5 ng/mL → high ovarian reserve (common in PCOS)
        const amh = parseFloat(hormoneData.amh) || 0;
        if (amh > 3.5) {
          score += 10;
          signals.push("Elevated AMH");
        }
      }
      
      // ── Cycle-based scoring ──
      if (activeCycleData) {
        hasData = true;
        const cycleLen = parseInt(activeCycleData.cycleLength) || 28;
        if (cycleLen > 35) {
          score += 15;
          signals.push("Long cycle length");
        }
      }
      
      // ── Symptom-based scoring ──
      const { data: recentSymptoms } = await supabase
        .from("symptom_logs")
        .select("*")
        .eq("user_id", uid)
        .order("log_date", { ascending: false })
        .limit(7);
      
      if (recentSymptoms && recentSymptoms.length > 0) {
        hasData = true;
        const avgAcne = recentSymptoms.reduce((sum, s) => sum + (s.acne || 0), 0) / recentSymptoms.length;
        const avgFatigue = recentSymptoms.reduce((sum, s) => sum + (s.fatigue || 0), 0) / recentSymptoms.length;
        const avgHairLoss = recentSymptoms.reduce((sum, s) => sum + (s.hair_loss || 0), 0) / recentSymptoms.length;
        
        if (avgAcne >= 1.5) {
          score += 10;
          signals.push("Acne symptoms");
        }
        if (avgFatigue >= 2) {
          score += 10;
          signals.push("Fatigue symptoms");
        }
        if (avgHairLoss >= 1.5) {
          score += 5;
          signals.push("Hair thinning");
        }
      }
      
      // Cap score at 100
      score = Math.min(score, 100);
      
      if (hasData) {
        setPcosPatternScore(score);
        setPcosSignals(signals);
        console.log('[HomeScreen] Updated PCOS score:', score, '| signals:', signals);
      } else {
        setPcosPatternScore(null);
        setPcosSignals([]);
        console.log('[HomeScreen] PCOS score: no data available');
      }
      
      // Calculate PCOS Health Snapshot using Supabase-sourced data
      calculateHealthSnapshot(
        activeCycleData ? JSON.stringify(activeCycleData) : null, 
        null, // period history — loaded by CycleScreen when needed
        null, // bleeding history
        null, // symptom pattern
        todayCheckin ? JSON.stringify(todayCheckin) : null, 
        hormoneData ? JSON.stringify(hormoneData) : null
      );
      
      // ── data loading complete ──
      setDataLoading(false);
      console.log('[HomeScreen] Data loading COMPLETE for user:', uid);
    };
    load();
  }, [userId]);

  // Setup OneSignal with user data
  const setupOneSignal = async () => {
    try {
      // Check if OneSignal is available and push is supported
      if (typeof window === 'undefined' || !window.OneSignalDeferred) {
        console.log('[OneSignal] SDK not available');
        return;
      }
      
      // Note: External user ID (Supabase user.id) is set in App.js on login
      // This function handles prompt display and tagging only
      
      // Check if already subscribed
      const isSubscribed = await isPushSubscribed();
      
      // Show notification prompt after a delay if not subscribed and not dismissed
      if (!isSubscribed && !notificationDismissed) {
        // Show prompt after 10 seconds of being on home screen.
        // Re-check the dismissed flag inside the timer callback so a
        // second (React.StrictMode double-mount) timer never resurrects
        // a banner the user already closed.
        setTimeout(() => {
          if (localStorage.getItem('pcosNotificationPromptDismissed') === 'true') return;
          setShowNotificationPrompt(true);
        }, 10000);
      }
      
      // Add tags based on onboarding data (non-blocking)
      const savedOnboarding = localStorage.getItem('pcosOnboarding');
      if (savedOnboarding) {
        const onboarding = JSON.parse(savedOnboarding);
        addTags({
          diagnosis_status: onboarding.diagnosis_status || 'unknown',
          primary_goal: onboarding.primary_goal || 'general',
          cycle_regularity: onboarding.cycle_regularity || 'unknown'
        });
      }
    } catch (error) {
      console.log('[OneSignal] Setup note:', error.message);
    }
  };

  // Handle notification prompt actions
  const handleEnableNotifications = async () => {
    const result = await requestNotificationPermission();
    // Any explicit answer from the user closes the prompt so the button
    // never appears "stuck". Only 'granted' triggers OneSignal handoff.
    if (result === 'granted') {
      await processPendingExternalUserId();
    } else if (result === 'unsupported') {
      alert('Notifications aren\u2019t supported by this browser. Please install the PCOS Insights PWA or open it in Chrome/Safari.');
    } else if (result === 'denied') {
      alert('Notifications are blocked. You can re-enable them from your browser\u2019s site settings.');
    }
    setShowNotificationPrompt(false);
    localStorage.setItem('pcosNotificationPromptDismissed', 'true');
    setNotificationDismissed(true);
  };

  const handleDismissNotificationPrompt = () => {
    setShowNotificationPrompt(false);
    localStorage.setItem('pcosNotificationPromptDismissed', 'true');
    setNotificationDismissed(true);
  };

  // Calculate PCOS Health Snapshot - Always shows if ANY data exists
  const calculateHealthSnapshot = (cycleDataRaw, periodHistoryRaw, bleedingHistoryRaw, symptomPatternRaw, recentCheckinRaw, hormoneDataRaw) => {
    const today = new Date().toISOString().split('T')[0];
    const snapshot = {
      hasAnyData: false,
      overallStatus: null,
      riskScore: 0,
      signals: [],
      recommendation: '',
      dataAvailable: {
        cycle: false,
        bleeding: false,
        symptoms: false,
        habits: false,
        hormones: false
      }
    };
    
    let riskPoints = 0;
    let maxPossiblePoints = 0;
    
    // Parse cycle data
    const cycleData = cycleDataRaw ? JSON.parse(cycleDataRaw) : null;
    if (cycleData && cycleData.cycleLength) {
      snapshot.hasAnyData = true;
      snapshot.dataAvailable.cycle = true;
      maxPossiblePoints += 30;
      
      const cycleLen = parseInt(cycleData.cycleLength);
      if (cycleLen > 35) {
        riskPoints += 15;
        snapshot.signals.push({ text: "Long cycle length", severity: 'moderate', category: 'cycle' });
      } else if (cycleLen < 21) {
        riskPoints += 10;
        snapshot.signals.push({ text: "Short cycle length", severity: 'mild', category: 'cycle' });
      }
      
      // Check if cycle is overdue
      if (cycleData.lastPeriodDate) {
        const lastPeriod = new Date(cycleData.lastPeriodDate);
        const daysSince = Math.floor((new Date() - lastPeriod) / 86400000);
        if (daysSince > cycleLen + 7) {
          riskPoints += 15;
          snapshot.signals.push({ text: `Period ${daysSince - cycleLen} days late`, severity: 'moderate', category: 'cycle' });
        }
      }
    }
    
    // Parse period history for irregularity
    const periodHistory = periodHistoryRaw ? JSON.parse(periodHistoryRaw) : [];
    if (periodHistory.length >= 2) {
      snapshot.hasAnyData = true;
      snapshot.dataAvailable.cycle = true;
      maxPossiblePoints += 20;
      
      const cycleLengths = [];
      for (let i = 1; i < periodHistory.length; i++) {
        const prev = new Date(periodHistory[i - 1].date);
        const curr = new Date(periodHistory[i].date);
        const days = Math.floor((curr - prev) / 86400000);
        if (days > 0 && days <= 90) cycleLengths.push(days);
      }
      
      if (cycleLengths.length >= 2) {
        const avg = cycleLengths.reduce((a, b) => a + b, 0) / cycleLengths.length;
        const variance = cycleLengths.reduce((sum, len) => sum + Math.pow(len - avg, 2), 0) / cycleLengths.length;
        const stdDev = Math.sqrt(variance);
        
        if (stdDev > 10) {
          riskPoints += 20;
          snapshot.signals.push({ text: "Highly irregular cycles", severity: 'high', category: 'cycle' });
        } else if (stdDev > 5) {
          riskPoints += 10;
          snapshot.signals.push({ text: "Moderately irregular cycles", severity: 'moderate', category: 'cycle' });
        }
      }
    }
    
    // Parse bleeding data
    const bleedingHistory = bleedingHistoryRaw ? JSON.parse(bleedingHistoryRaw) : [];
    if (bleedingHistory.length > 0) {
      snapshot.hasAnyData = true;
      snapshot.dataAvailable.bleeding = true;
      maxPossiblePoints += 15;
      
      const latest = bleedingHistory[bleedingHistory.length - 1];
      if (latest.duration && latest.duration > 7) {
        riskPoints += 10;
        snapshot.signals.push({ text: "Prolonged bleeding", severity: 'moderate', category: 'bleeding' });
      }
      if (latest.flow === 'very_heavy' || latest.flow === 'heavy') {
        riskPoints += 5;
        snapshot.signals.push({ text: "Heavy menstrual flow", severity: 'mild', category: 'bleeding' });
      }
      if (latest.pain === 'severe') {
        riskPoints += 10;
        snapshot.signals.push({ text: "Severe menstrual pain", severity: 'moderate', category: 'bleeding' });
      } else if (latest.pain === 'moderate') {
        riskPoints += 5;
        snapshot.signals.push({ text: "Moderate menstrual pain", severity: 'mild', category: 'bleeding' });
      }
    }
    
    // Parse symptom pattern
    const symptomPattern = symptomPatternRaw ? JSON.parse(symptomPatternRaw) : null;
    if (symptomPattern && symptomPattern.averages) {
      snapshot.hasAnyData = true;
      snapshot.dataAvailable.symptoms = true;
      maxPossiblePoints += 25;
      
      const avgs = symptomPattern.averages;
      if (avgs.acne && avgs.acne >= 2) {
        riskPoints += 8;
        snapshot.signals.push({ text: "Persistent acne", severity: avgs.acne >= 2.5 ? 'moderate' : 'mild', category: 'symptoms' });
      }
      if (avgs.hair_loss && avgs.hair_loss >= 2) {
        riskPoints += 8;
        snapshot.signals.push({ text: "Hair thinning", severity: avgs.hair_loss >= 2.5 ? 'moderate' : 'mild', category: 'symptoms' });
      }
      if (avgs.fatigue && avgs.fatigue >= 2) {
        riskPoints += 5;
        snapshot.signals.push({ text: "Chronic fatigue", severity: avgs.fatigue >= 2.5 ? 'moderate' : 'mild', category: 'symptoms' });
      }
      if (avgs.mood && avgs.mood >= 2) {
        riskPoints += 4;
        snapshot.signals.push({ text: "Mood fluctuations", severity: 'mild', category: 'symptoms' });
      }
    }
    
    // Parse recent check-in
    const recentCheckin = recentCheckinRaw ? JSON.parse(recentCheckinRaw) : null;
    if (recentCheckin) {
      snapshot.hasAnyData = true;
      snapshot.dataAvailable.symptoms = true;
      if (!symptomPattern) maxPossiblePoints += 15;
      
      if (recentCheckin.acne >= 2 && !snapshot.signals.some(s => s.text.includes("acne"))) {
        riskPoints += 5;
        snapshot.signals.push({ text: "Recent acne flare", severity: recentCheckin.acne >= 3 ? 'moderate' : 'mild', category: 'symptoms' });
      }
      if (recentCheckin.fatigue >= 2 && !snapshot.signals.some(s => s.text.includes("fatigue"))) {
        riskPoints += 3;
        snapshot.signals.push({ text: "Feeling fatigued today", severity: 'mild', category: 'symptoms' });
      }
    }
    
    // Check daily habits from state (not localStorage)
    // habits state is already loaded from Supabase in the main load function
    if (habits) {
      const completedCount = Object.values(habits).filter(Boolean).length;
      if (completedCount > 0) {
        snapshot.hasAnyData = true;
        snapshot.dataAvailable.habits = true;
      }
    }
    
    // Parse hormone data
    const hormoneData = hormoneDataRaw ? JSON.parse(hormoneDataRaw) : null;
    if (hormoneData) {
      snapshot.hasAnyData = true;
      snapshot.dataAvailable.hormones = true;
      maxPossiblePoints += 40;
      
      const ratio = hormoneData.ratio || (hormoneData.lh && hormoneData.fsh ? hormoneData.lh / hormoneData.fsh : null);
      if (ratio && ratio > 2) {
        riskPoints += 15;
        snapshot.signals.push({ text: "Elevated LH/FSH ratio", severity: ratio > 2.5 ? 'high' : 'moderate', category: 'hormones' });
      }
      
      // Testosterone > 70
      const testo = parseFloat(hormoneData.testosterone) || 0;
      if (testo > 70) {
        riskPoints += 12;
        snapshot.signals.push({ text: "Elevated testosterone", severity: testo > 85 ? 'high' : 'moderate', category: 'hormones' });
      }
      
      // Insulin > 12
      const ins = parseFloat(hormoneData.insulin) || 0;
      if (ins > 12) {
        riskPoints += 10;
        snapshot.signals.push({ text: "Elevated insulin (resistance indicator)", severity: ins > 20 ? 'high' : 'moderate', category: 'hormones' });
      }
      
      // AMH > 3.5
      const amh = parseFloat(hormoneData.amh) || 0;
      if (amh > 3.5) {
        riskPoints += 8;
        snapshot.signals.push({ text: "Elevated AMH", severity: amh > 5 ? 'high' : 'moderate', category: 'hormones' });
      }
    }
    
    // Calculate overall status
    if (snapshot.hasAnyData) {
      const riskPercentage = maxPossiblePoints > 0 ? (riskPoints / maxPossiblePoints) * 100 : 0;
      snapshot.riskScore = Math.min(Math.round(riskPercentage), 100);
      
      if (riskPercentage <= 25) {
        snapshot.overallStatus = 'low';
        snapshot.recommendation = "Your patterns look healthy! Keep tracking to maintain awareness.";
      } else if (riskPercentage <= 55) {
        snapshot.overallStatus = 'moderate';
        snapshot.recommendation = "Some patterns need attention. Consider discussing with your healthcare provider.";
      } else {
        snapshot.overallStatus = 'high';
        snapshot.recommendation = "Several patterns indicate potential PCOS concerns. We recommend consulting a healthcare professional.";
      }
      
      // Customize recommendation based on missing data
      const missing = [];
      if (!snapshot.dataAvailable.cycle) missing.push("cycle");
      if (!snapshot.dataAvailable.symptoms) missing.push("symptoms");
      if (!snapshot.dataAvailable.hormones) missing.push("hormone reports");
      
      if (missing.length > 0 && missing.length < 3) {
        snapshot.recommendation += ` Add ${missing.join(" and ")} data for a more complete picture.`;
      }
    }
    
    setHealthSnapshot(snapshot);
  };

  // FEATURE 5: Handle Check-In Save
  const handleCheckinSave = async () => {
    const checkinData = {
      date: new Date().toISOString(),
      ...checkinScores
    };
    
    // Save to Supabase via service
    await saveCheckin(userId, checkinData);
    
    setShowCheckinModal(false);
    setCheckinComplete(true);
  };

  // Habit toggle handler
  const toggleHabit = async (key) => {
    const newHabits = { ...habits, [key]: !habits[key] };
    setHabits(newHabits);
    
    // Save to Supabase via service
    await saveHabits(userId, newHabits);
  };

  // Calculate habit progress
  const habitProgress = Object.values(habits).filter(Boolean).length / habitItems.length;

  const getTimeOfDay = () => {
    const h = new Date().getHours();
    if (h < 12) return "morning";
    if (h < 17) return "afternoon";
    return "evening";
  };

  const getInsightText = () => {
    // Hormone-pattern intelligence — sourced from Supabase-derived
    // `hormonePattern` (via intelligenceService); legacy
    // `localStorage.pcosHormonePattern` was dead code (no writer).
    const symptomPatternData = localStorage.getItem('pcosSymptomPattern');
    
    // Get recent check-ins for fatigue analysis
    const checkinKeys = Object.keys(localStorage).filter(k => k.startsWith('pcosCheckin_')).slice(-7);
    const recentCheckins = checkinKeys.map(k => JSON.parse(localStorage.getItem(k)));
    const avgFatigue = recentCheckins.length > 0 
      ? recentCheckins.reduce((s, c) => s + (c.fatigue || 0), 0) / recentCheckins.length 
      : 0;
    
    // Smart insight generation
    if (hormonePattern) {
      // Insulin resistance pattern with high fatigue
      if (hormonePattern.tags?.includes('insulin_resistant') && avgFatigue >= 2) {
        return "Your hormone pattern suggests insulin resistance, and recent fatigue levels are elevated. Consider discussing metabolic health with your provider.";
      }
      
      // Classic PCOS pattern — 2+ tags flagged
      if ((hormonePattern.tags?.length || 0) >= 2) {
        return "Several hormone markers indicate a classic PCOS pattern. Regular monitoring and provider consultation recommended.";
      }
      
      // Moderate pattern with symptom data
      if ((hormonePattern.tags?.length || 0) === 1 && symptomPatternData) {
        return "Some hormone markers are worth monitoring. Your symptom logs help track patterns over time.";
      }
      
      // Normal hormones
      if ((hormonePattern.tags?.length || 0) === 0) {
        if (hormoneInsight !== null && hormoneInsight <= 1) {
          return "Hormone balance appears stable this week. Keep up your healthy habits.";
        }
      }
    }
    
    // Fallback messages based on data availability
    if (hormoneInsight === null) {
      return "Upload your first blood report to unlock personalized hormone insights.";
    }
    
    if (hormoneInsight <= 1) {
      return "Hormone balance appears stable this week. Keep up your healthy habits.";
    }
    
    if (hormoneInsight <= 3) {
      return "A few markers are worth monitoring. Log your symptoms regularly.";
    }
    
    return "Several hormone markers flagged this week. A provider consultation is recommended.";
  };

  const handleQuizSubmit = () => {
    const yesCount = Object.values(quizAnswers).filter((a) => a === "yes").length;
    if (yesCount <= 1)
      setQuizResult("Your responses don't suggest a strong PCOS symptom pattern. Routine check-ups are always a good idea.");
    else if (yesCount <= 3)
      setQuizResult("Some patterns may be worth discussing with your doctor. Everyone's experience is different.");
    else
      setQuizResult("Several commonly reported PCOS symptoms noted. A clinical evaluation is recommended.");
  };

  const todayFormatted = new Date().toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric",
  });

  // Generate dynamic PCOS insight based on cycle phase, symptoms, and hormone patterns
  const getTodayInsight = () => {
    const insights = [];
    const timeOfDay = getTimeOfDay();
    
    // Get symptom data for context
    const symptomPatternData = localStorage.getItem('pcosSymptomPattern');
    const symptomPattern = symptomPatternData ? JSON.parse(symptomPatternData) : null;
    const hasFatigue = symptomPattern?.averages?.fatigue >= 2 || todayLog?.fatigue >= 2;
    const hasAcne = symptomPattern?.averages?.acne >= 2 || todayLog?.acne >= 2;
    const hasMoodIssues = symptomPattern?.averages?.mood >= 2 || todayLog?.mood >= 2;
    
    // Highly personalized cycle phase insights
    if (cyclePhase) {
      switch (cyclePhase) {
        case "Period":
          if (hasFatigue) {
            insights.push({
              title: "Rest Day Priority",
              text: `You're on your period and feeling fatigued. This is your body asking for rest. Take it easy this ${timeOfDay}.`,
              icon: "🩸"
            });
          } else {
            insights.push({
              title: "Menstrual Phase Care",
              text: "Your body is working hard right now. Warm foods, iron-rich meals, and gentle movement will support you today.",
              icon: "🩸"
            });
          }
          break;
        case "Follicular Phase":
          if (hasFatigue) {
            insights.push({
              title: "Energy Building",
              text: "You're in your follicular phase — energy should be rising. If you're still tired, focus on protein and B-vitamins today.",
              icon: "🌱"
            });
          } else {
            insights.push({
              title: "Peak Opportunity",
              text: "You're in your follicular phase — this is your power window! Great day for workouts, planning, or starting something new.",
              icon: "🌱"
            });
          }
          break;
        case "Ovulation":
          if (hasAcne) {
            insights.push({
              title: "Ovulation Skin Care",
              text: "You're ovulating and may notice skin changes. Stay hydrated and avoid dairy today to help manage breakouts.",
              icon: "✨"
            });
          } else {
            insights.push({
              title: "Peak Energy Day",
              text: "You're ovulating — confidence and energy are at their highest! Perfect time for presentations, dates, or tough conversations.",
              icon: "✨"
            });
          }
          break;
        case "Luteal Phase":
          if (hasFatigue) {
            insights.push({
              title: "Luteal Rest Mode",
              text: "You're in luteal phase — fatigue is completely normal now. Prioritize rest and don't push too hard today.",
              icon: "🌙"
            });
          } else if (hasMoodIssues) {
            insights.push({
              title: "Mood Support",
              text: "You're in luteal phase — mood shifts are hormonal, not personal. Magnesium-rich foods and early bedtime can help.",
              icon: "🌙"
            });
          } else {
            insights.push({
              title: "Luteal Phase Care",
              text: "You're in luteal phase — your body is preparing. Complex carbs, warm meals, and cozy activities are your friends today.",
              icon: "🌙"
            });
          }
          break;
      }
    }
    
    // Hormone pattern + symptom combination insights
    if (hormonePatternName) {
      if (hormonePatternName.includes("Insulin")) {
        if (timeOfDay === "morning") {
          insights.push({
            title: "Morning Strategy",
            text: "With insulin resistance patterns, start your day with protein, not carbs. This sets your blood sugar up for success.",
            icon: "🥚"
          });
        } else {
          insights.push({
            title: "Blood Sugar Balance",
            text: "Your hormone pattern suggests insulin sensitivity. A short walk after meals can significantly help today.",
            icon: "🚶‍♀️"
          });
        }
      } else if (hormonePatternName.includes("Classic PCOS") || hormonePatternName.includes("Androgenic")) {
        if (hasAcne) {
          insights.push({
            title: "Androgen Management",
            text: "Your pattern shows elevated androgens affecting your skin. Spearmint tea and zinc may help — try one today.",
            icon: "🌿"
          });
        } else {
          insights.push({
            title: "Hormone Balance",
            text: "Focus on stress management today — cortisol spikes can worsen androgen levels. Even 5 minutes of deep breathing helps.",
            icon: "🧘‍♀️"
          });
        }
      }
    }
    
    // Symptom-specific personalized insights (no phase data)
    if (!cyclePhase) {
      if (hasFatigue) {
        insights.push({
          title: "Energy Support",
          text: "You've been experiencing fatigue. Check your iron levels, stay hydrated, and consider B-complex vitamins.",
          icon: "⚡"
        });
      }
      if (hasAcne) {
        insights.push({
          title: "Skin Clarity",
          text: "Persistent acne often links to androgens. Anti-inflammatory foods like salmon, walnuts, and berries may help.",
          icon: "🫐"
        });
      }
      if (hasMoodIssues) {
        insights.push({
          title: "Mood Wellness",
          text: "Mood fluctuations are common with PCOS. Omega-3s, regular sleep, and limiting sugar can make a real difference.",
          icon: "🌈"
        });
      }
    }
    
    // Default insight if no data - personalize based on onboarding goal
    if (insights.length === 0) {
      // Use onboarding data to personalize default insights
      if (onboardingData?.primary_goal) {
        const goalInsights = {
          periods: [
            { title: "Cycle Focus", text: "You're working on regulating your periods. Consistent sleep schedules and stress management are key — try one tonight.", icon: "🩸" },
            { title: "Period Regulation", text: "For cycle regularity, consider tracking your basal body temperature. It reveals a lot about your hormonal rhythm.", icon: "📊" }
          ],
          weight: [
            { title: "Weight Management", text: "Your goal is weight management. PCOS makes this harder, but not impossible. Focus on strength training over cardio today.", icon: "⚖️" },
            { title: "Metabolic Support", text: "With PCOS, insulin affects weight. Try eating protein first at each meal — it helps with blood sugar balance.", icon: "🥗" }
          ],
          skin_hair: [
            { title: "Skin & Hair Care", text: "You're focused on skin and hair health. Androgens are often the culprit — anti-inflammatory foods can help.", icon: "✨" },
            { title: "Clear Skin Strategy", text: "For PCOS-related skin issues, spearmint tea has anti-androgen properties. Try a cup today!", icon: "🌿" }
          ],
          fertility: [
            { title: "Fertility Support", text: "You're focusing on fertility. Tracking ovulation signs and maintaining healthy weight improves conception chances.", icon: "🌸" },
            { title: "Conception Focus", text: "For fertility, cycle awareness is key. Log your symptoms daily to understand your fertile window better.", icon: "💚" }
          ],
          general: [
            { title: "Wellness Journey", text: "You're on a general wellness path. Small daily habits — sleep, movement, nutrition — compound into big changes.", icon: "💜" },
            { title: "Holistic Health", text: "For overall PCOS management, balance is everything. Track how you feel to find your personal patterns.", icon: "🌈" }
          ]
        };
        
        const goalMessages = goalInsights[onboardingData.primary_goal] || goalInsights.general;
        const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
        return goalMessages[dayOfYear % goalMessages.length];
      }
      
      const defaultInsights = [
        {
          title: "Start Your Journey",
          text: "Log your cycle and symptoms to unlock personalized daily guidance tailored just for you.",
          icon: "💜"
        },
        {
          title: "Knowledge is Power",
          text: "Understanding your unique PCOS pattern starts with data. Track today, understand tomorrow.",
          icon: "📊"
        },
        {
          title: "You're Not Alone",
          text: "1 in 10 women have PCOS. Your journey matters — start tracking to take control of your health.",
          icon: "💪"
        }
      ];
      const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
      return defaultInsights[dayOfYear % defaultInsights.length];
    }
    
    // Return the most relevant insight (first one, or rotate if multiple)
    const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
    return insights[dayOfYear % insights.length];
  };

  const todayInsight = getTodayInsight();

  // Show loading while data is being fetched from Supabase
  if (dataLoading) {
    return (
      <div className="home-screen" data-testid="home-screen-loading" style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', minHeight: '60vh', gap: 18
      }}>
        <TheoOrb state="thinking" size="md" />
        <p style={{ color: '#8A8A9A', fontSize: 14 }}>Loading your data...</p>
      </div>
    );
  }

  return (
    <div className="home-screen fade-in" data-testid="home-screen">
      {/* Push Notification Prompt */}
      {showNotificationPrompt && (
        <div style={{
          background: 'linear-gradient(135deg, #FEF3C7, #FDE68A)',
          borderRadius: 16,
          padding: '16px 18px',
          marginBottom: 16,
          border: '1px solid #FCD34D',
          boxShadow: '0 2px 12px rgba(251,191,36,0.15)'
        }} data-testid="notification-prompt">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #F59E0B, #D97706)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
              flexShrink: 0
            }}>
              🔔
            </div>
            <div style={{ flex: 1 }}>
              <h4 style={{ fontSize: 14, fontWeight: 700, color: '#92400E', margin: '0 0 4px 0' }}>
                Stay on Track with Reminders
              </h4>
              <p style={{ fontSize: 13, color: '#B45309', margin: '0 0 12px 0', lineHeight: 1.5 }}>
                Get daily insights, cycle reminders, and health tips delivered right to you.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={handleEnableNotifications}
                  style={{
                    padding: '8px 16px',
                    fontSize: 13,
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #A78BFA, #6D5EF5)',
                    border: 'none',
                    borderRadius: 8,
                    color: '#fff',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(109,94,245,0.3)'
                  }}
                  data-testid="enable-notifications-btn"
                >
                  Enable Notifications
                </button>
                <button
                  onClick={handleDismissNotificationPrompt}
                  style={{
                    padding: '8px 12px',
                    fontSize: 13,
                    fontWeight: 500,
                    background: 'transparent',
                    border: '1px solid #D97706',
                    borderRadius: 8,
                    color: '#B45309',
                    cursor: 'pointer'
                  }}
                  data-testid="dismiss-notifications-btn"
                >
                  Not Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 1. Greeting Section */}
      <div className="home-greeting" style={{ marginBottom: 20 }}>
        <h1 data-testid="greeting" style={{ fontSize: 26, marginBottom: 4 }}>Good {getTimeOfDay()}, {userName}</h1>
        <p style={{ color: '#8A8A9A', fontSize: 14, margin: 0 }}>Let's understand your hormones today.</p>
      </div>

      {/* 1b. Theo card — premium entry to the AI companion */}
      {(() => {
        // Smarter, more observant copy
        let observation = "Theo has a new observation for you.";
        if (lastReportStatus === "needs_update")
          observation = "It's been a while since your last hormone report — I'd love a fresh look.";
        else if (cyclePhase === "luteal")
          observation = "I noticed your energy usually dips during the luteal phase.";
        else if (cyclePhase === "follicular")
          observation = "You're entering your most energizing window of the month.";
        else if (cyclePhase === "menstrual")
          observation = "Period day — your body is doing real work this week.";
        else if (cyclePhase === "ovulation")
          observation = "You're around ovulation — a great window for clarity.";
        else if (todayLog)
          observation = "Your recent logs have a pattern worth talking about.";
        else if (cyclePhase)
          observation = `You're in your ${cyclePhase} phase — let's check in.`;

        return (
          <div
            data-testid="home-theo-card"
            onClick={() => onNavigate?.("theo")}
            style={{
              position: "relative",
              background: "#FFFFFF",
              borderRadius: 22,
              padding: 18,
              marginBottom: 20,
              cursor: "pointer",
              display: "flex",
              gap: 14,
              alignItems: "center",
              boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.04)",
              border: "1px solid #F1F5F9",
              overflow: "hidden",
              transition: "transform 0.18s ease",
            }}
            onMouseDown={(e) => e.currentTarget.style.transform = "scale(0.99)"}
            onMouseUp={(e) => e.currentTarget.style.transform = "scale(1)"}
            onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
          >
            {/* Theo orb — V8 living nebula in glass */}
            <div style={{ flexShrink: 0, position: "relative", width: 56, height: 56, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <TheoOrb state="idle" size="sm" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 10.5, fontWeight: 800, color: "#7C3AED", letterSpacing: "0.12em", marginBottom: 4 }}>
                THEO
              </div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: "#0F172A", lineHeight: 1.35, marginBottom: 4, letterSpacing: "-0.005em" }}>
                {observation}
              </div>
              <div style={{ fontSize: 12, color: "#64748B", display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ color: "#7C3AED", fontWeight: 700 }}>Talk to Theo</span>
                <span>›</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* 2. Unified Current Cycle + Today's Insight Card - MOST PROMINENT */}
      {(() => {
        const cycleMeta = (() => {
          const parts = [];
          if (cycleLengthAvg) parts.push(`${cycleLengthAvg}-day cycle average`);
          if (cyclesTracked >= 2) parts.push(`${cyclesTracked} cycles tracked`);
          if (parts.length) return parts.join(' · ');
          // Fallback: user has an active cycle but no reliable history yet.
          return cycleDay && cyclePhase ? 'Track more cycles to reveal your pattern' : null;
        })();
        const phaseGlyph = (() => {
          const stroke = 'rgba(255,240,235,0.86)';
          if (cyclePhase === 'Period') {
            return (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="#B96A73" aria-hidden="true">
                <path d="M12 3c-.3 0-.58.14-.77.38C9.3 5.8 5 11.36 5 15a7 7 0 0 0 14 0c0-3.64-4.3-9.2-6.23-11.62A1 1 0 0 0 12 3z" />
              </svg>
            );
          }
          if (cyclePhase === 'Follicular Phase') {
            return (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={stroke}
                   strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 20V9" />
                <path d="M12 9c0-3.5 2-6 5-6-.5 3.5-2.5 6-5 6z" />
                <path d="M12 13c0-2.5-1.5-4.5-4-4.5.4 2.5 1.7 4.5 4 4.5z" />
              </svg>
            );
          }
          if (cyclePhase === 'Ovulation') {
            return (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={stroke}
                   strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="3.2" fill={stroke} stroke="none" />
                <path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.6 5.6l1.8 1.8M16.6 16.6l1.8 1.8M18.4 5.6l-1.8 1.8M7.4 16.6l-1.8 1.8" />
              </svg>
            );
          }
          if (cyclePhase === 'Luteal Phase') {
            return (
              <svg width="22" height="22" viewBox="0 0 24 24" fill={stroke} aria-hidden="true">
                <path d="M20 14.5A8.5 8.5 0 1 1 10.2 4a7 7 0 0 0 9.8 10.5z" />
              </svg>
            );
          }
          return (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={stroke}
                 strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="5" width="18" height="16" rx="2.5" />
              <path d="M8 3v4M16 3v4M3 10h18" />
            </svg>
          );
        })();
        return (
      <div style={{
        position: 'relative',
        background: '#30263F',
        borderRadius: 22,
        padding: '22px 24px 20px',
        marginBottom: 20,
        color: '#F6EEF0',
        boxShadow: '0 10px 28px rgba(48,38,63,0.32), 0 1px 2px rgba(0,0,0,0.06)',
        border: '1px solid rgba(255,255,255,0.04)',
        overflow: 'hidden',
        isolation: 'isolate',
      }} data-testid="cycle-phase-card">
        {/* Soft ambient depth — restrained, static */}
        <div aria-hidden="true" style={{
          position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0,
          background: [
            'radial-gradient(110% 80% at 88% 0%, rgba(98,82,118,0.55) 0%, rgba(98,82,118,0) 55%)',
            'radial-gradient(90% 70% at 0% 100%, rgba(73,56,95,0.45) 0%, rgba(73,56,95,0) 60%)',
            'linear-gradient(180deg, rgba(255,255,255,0.02), rgba(0,0,0,0.10))',
          ].join(', '),
        }} />
        {/* Top translucent edge highlight — very subtle premium material cue */}
        <div aria-hidden="true" style={{
          position: 'absolute', top: 0, left: 20, right: 20, height: 1,
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.10), transparent)',
          zIndex: 1,
        }} />

        {/* Content layer */}
        <div style={{ position: 'relative', zIndex: 2 }}>
          {/* Primary — cycle status */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontSize: 10.5, fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.16em',
                color: 'rgba(246,238,240,0.62)',
                marginBottom: 8,
              }}>
                Current Cycle
              </div>
              {cycleDay && cyclePhase ? (
                <>
                  <div style={{
                    display: 'flex', alignItems: 'baseline', gap: 10,
                    lineHeight: 1.05,
                  }}>
                    <span style={{ fontSize: 34, fontWeight: 600, letterSpacing: '-0.015em' }}>
                      Day {cycleDay}
                    </span>
                    <span style={{
                      fontSize: 14, fontWeight: 500,
                      color: 'rgba(246,238,240,0.76)',
                    }}>
                      · {cyclePhase}
                    </span>
                  </div>
                  {cycleMeta && (
                    <div style={{
                      marginTop: 6,
                      fontSize: 11.5, fontWeight: 400,
                      color: 'rgba(246,238,240,0.52)',
                      letterSpacing: '0.005em',
                    }}>
                      {cycleMeta}
                    </div>
                  )}
                </>
              ) : (
                <div
                  style={{ fontSize: 15, color: 'rgba(246,238,240,0.85)', cursor: 'pointer' }}
                  onClick={() => onNavigate('cycle')}
                >
                  Add cycle data to see your phase →
                </div>
              )}
            </div>
            <div style={{
              width: 52, height: 52, borderRadius: '50%',
              background: cyclePhase === 'Period'
                ? 'rgba(185,106,115,0.14)'
                : 'rgba(255,255,255,0.09)',
              border: `1px solid ${cyclePhase === 'Period' ? 'rgba(185,106,115,0.28)' : 'rgba(255,255,255,0.10)'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
              flexShrink: 0,
            }}>
              {phaseGlyph}

            </div>
          </div>

          {/* Divider */}
          <div style={{
            height: 1,
            background: 'linear-gradient(90deg, rgba(255,255,255,0.02), rgba(255,255,255,0.14) 30%, rgba(255,255,255,0.14) 70%, rgba(255,255,255,0.02))',
            margin: '18px 0 16px',
          }} />

          {/* Secondary — Today's Insight, editorial text block */}
          <div data-testid="today-insight-card">
            <div style={{
              fontSize: 10.5, fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.16em',
              color: 'rgba(246,238,240,0.62)',
              marginBottom: 6,
            }}>
              Today's Insight
            </div>
            <div style={{
              fontSize: 15.5, fontWeight: 600,
              letterSpacing: '-0.005em',
              lineHeight: 1.3,
              marginBottom: 5,
            }}>
              {todayInsight.title}
            </div>
            <p style={{
              fontSize: 13, fontWeight: 400,
              color: 'rgba(246,238,240,0.78)',
              lineHeight: 1.5,
              margin: 0,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
              {todayInsight.text}
            </p>
          </div>
        </div>
      </div>
        );
      })()}

      {/* 3. PCOS Pattern Score Card - SECOND PRIORITY */}
      <div style={{
        background: 'linear-gradient(135deg, #FAFAFF 0%, #F5F3FF 50%, #EDE9FE 100%)',
        borderRadius: 20,
        padding: 20,
        marginBottom: 20,
        boxShadow: '0 4px 20px rgba(109,94,245,0.12)',
        border: '1px solid rgba(167, 139, 250, 0.2)'
      }} data-testid="pcos-pattern-score-card">
        <div style={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 14
        }}>
          <div style={{ 
            fontSize: 11, 
            fontWeight: 700, 
            color: '#8B5CF6', 
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            PCOS Pattern Score
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            background: 'rgba(139, 92, 246, 0.1)',
            padding: '3px 8px',
            borderRadius: 6
          }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" strokeWidth="2.5">
              <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8z"/>
              <path d="M12 6v6l4 2"/>
            </svg>
            <span style={{ fontSize: 9, fontWeight: 600, color: '#7C3AED' }}>AI-Based Insight</span>
          </div>
        </div>
        
        {pcosPatternScore !== null ? (
          <>
            <div style={{ 
              display: 'flex', 
              alignItems: 'baseline', 
              gap: 6,
              marginBottom: 10
            }}>
              <span style={{ 
                fontSize: 56, 
                fontWeight: 800, 
                color: pcosPatternScore <= 30 ? '#10B981' : pcosPatternScore <= 70 ? '#F59E0B' : '#EF4444',
                lineHeight: 1,
                letterSpacing: '-2px'
              }}>
                {pcosPatternScore}
              </span>
              <span style={{ fontSize: 20, color: '#9CA3AF', fontWeight: 500 }}>/ 100</span>
            </div>
            
            <p style={{ 
              fontSize: 15, 
              fontWeight: 600,
              color: pcosPatternScore <= 30 ? '#059669' : pcosPatternScore <= 70 ? '#D97706' : '#DC2626',
              marginBottom: 14
            }}>
              {pcosPatternScore <= 30 
                ? "Low PCOS pattern signals detected" 
                : pcosPatternScore <= 70 
                  ? "Moderate PCOS pattern signals detected"
                  : "Strong PCOS pattern signals detected"}
            </p>
            
            {pcosSignals.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 6 }}>Detected signals:</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {pcosSignals.slice(0, 4).map((signal, idx) => (
                    <span 
                      key={idx}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        background: 'rgba(255,255,255,0.8)',
                        padding: '4px 10px',
                        borderRadius: 12,
                        fontSize: 12,
                        color: '#374151'
                      }}
                    >
                      <span style={{ color: '#A78BFA' }}>•</span>
                      {signal}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            <div style={{
              height: 8,
              background: 'rgba(255,255,255,0.6)',
              borderRadius: 4,
              overflow: 'hidden',
              marginBottom: 14
            }}>
              <div style={{
                height: '100%',
                width: `${pcosPatternScore}%`,
                background: pcosPatternScore <= 30 
                  ? 'linear-gradient(90deg, #34D399, #10B981)' 
                  : pcosPatternScore <= 70 
                    ? 'linear-gradient(90deg, #FBBF24, #F59E0B)'
                    : 'linear-gradient(90deg, #F87171, #EF4444)',
                borderRadius: 4,
                transition: 'width 0.5s ease-out'
              }} />
            </div>
            
            {/* CTA Button */}
            <button
              onClick={() => onNavigate('insights')}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: 'linear-gradient(135deg, #A78BFA, #8B5CF6)',
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)',
                transition: 'all 0.2s ease'
              }}
              data-testid="view-analysis-btn"
            >
              View Detailed Analysis
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{ 
              width: 48, 
              height: 48, 
              borderRadius: '50%', 
              background: 'rgba(139, 92, 246, 0.1)', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              margin: '0 auto 12px',
              fontSize: 24
            }}>
              📊
            </div>
            <p style={{ fontSize: 14, color: '#6B7280', margin: '0 0 14px 0' }}>
              Add your data to generate your PCOS pattern score
            </p>
            <button
              onClick={() => onNavigate('symptoms')}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #A78BFA, #8B5CF6)',
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)'
              }}
            >
              Start Tracking
            </button>
          </div>
        )}
        
        <p style={{ 
          fontSize: 10, 
          color: '#9CA3AF', 
          margin: '14px 0 0 0',
          textAlign: 'center',
          fontStyle: 'italic'
        }}>
          Educational insight only — not a medical diagnosis
        </p>
      </div>

      {/* PCOS Health Snapshot Card - TERTIARY (reduced emphasis) */}
      <div style={{
        background: healthSnapshot.hasAnyData 
          ? healthSnapshot.overallStatus === 'low' 
            ? '#F0FDF4'
            : healthSnapshot.overallStatus === 'moderate'
              ? '#FFFBEB'
              : '#FEF2F2'
          : '#FAFAFA',
        borderRadius: 16,
        padding: 16,
        marginBottom: 20,
        boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
        border: healthSnapshot.hasAnyData 
          ? healthSnapshot.overallStatus === 'low'
            ? '1px solid #86EFAC'
            : healthSnapshot.overallStatus === 'moderate'
              ? '1px solid #FCD34D'
              : '1px solid #FCA5A5'
          : '1px solid #E5E7EB'
      }} data-testid="pcos-health-snapshot-card">
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: 10
        }}>
          <div style={{ 
            fontSize: 10, 
            fontWeight: 600, 
            color: healthSnapshot.hasAnyData 
              ? healthSnapshot.overallStatus === 'low' ? '#166534'
                : healthSnapshot.overallStatus === 'moderate' ? '#A16207'
                : '#B91C1C'
              : '#6B7280',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Health Snapshot
          </div>
          {healthSnapshot.hasAnyData && (
            <div style={{
              display: 'flex',
              gap: 3
            }}>
              {healthSnapshot.dataAvailable.cycle && (
                <span style={{ fontSize: 9, background: 'rgba(255,255,255,0.8)', padding: '2px 5px', borderRadius: 4 }}>📅</span>
              )}
              {healthSnapshot.dataAvailable.bleeding && (
                <span style={{ fontSize: 9, background: 'rgba(255,255,255,0.8)', padding: '2px 5px', borderRadius: 4 }}>🩸</span>
              )}
              {healthSnapshot.dataAvailable.symptoms && (
                <span style={{ fontSize: 9, background: 'rgba(255,255,255,0.8)', padding: '2px 5px', borderRadius: 4 }}>📋</span>
              )}
              {healthSnapshot.dataAvailable.hormones && (
                <span style={{ fontSize: 9, background: 'rgba(255,255,255,0.8)', padding: '2px 5px', borderRadius: 4 }}>🧬</span>
              )}
            </div>
          )}
        </div>
        
        {healthSnapshot.hasAnyData ? (
          <>
            {/* Overall Status - Reduced size */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 10,
              marginBottom: 12
            }}>
              <div style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: healthSnapshot.overallStatus === 'low' 
                  ? 'linear-gradient(135deg, #10B981, #059669)'
                  : healthSnapshot.overallStatus === 'moderate'
                    ? 'linear-gradient(135deg, #F59E0B, #D97706)'
                    : 'linear-gradient(135deg, #EF4444, #DC2626)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <span style={{ color: '#fff', fontSize: 16 }}>
                  {healthSnapshot.overallStatus === 'low' ? '✓' : 
                   healthSnapshot.overallStatus === 'moderate' ? '!' : '!!'}
                </span>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ 
                  fontSize: 17, 
                  fontWeight: 600, 
                  color: healthSnapshot.overallStatus === 'low' ? '#166534'
                    : healthSnapshot.overallStatus === 'moderate' ? '#A16207'
                    : '#B91C1C',
                  marginBottom: 1
                }}>
                  {healthSnapshot.overallStatus === 'low' ? 'Low Risk' : 
                   healthSnapshot.overallStatus === 'moderate' ? 'Moderate Risk' : 'High Risk'}
                </div>
                <div style={{ 
                  fontSize: 11, 
                  color: healthSnapshot.overallStatus === 'low' ? '#166534'
                    : healthSnapshot.overallStatus === 'moderate' ? '#A16207'
                    : '#B91C1C'
                }}>
                  {healthSnapshot.signals.length} signal{healthSnapshot.signals.length !== 1 ? 's' : ''} detected
                </div>
              </div>
            </div>
            
            {/* Key Signals - Compact */}
            {healthSnapshot.signals.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {healthSnapshot.signals.slice(0, 3).map((signal, idx) => (
                    <span 
                      key={idx}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '3px 8px',
                        background: 'rgba(255,255,255,0.8)',
                        borderRadius: 8,
                        fontSize: 11,
                        color: '#4B5563'
                      }}
                    >
                      <span style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: signal.severity === 'high' ? '#EF4444'
                          : signal.severity === 'moderate' ? '#F59E0B'
                          : '#10B981'
                      }} />
                      {signal.text}
                    </span>
                  ))}
                  {healthSnapshot.signals.length > 3 && (
                    <span style={{ 
                      fontSize: 11, 
                      color: '#6B7280',
                      padding: '3px 6px'
                    }}>
                      +{healthSnapshot.signals.length - 3} more
                    </span>
                  )}
                </div>
              </div>
            )}
            
            {/* Recommendation - Compact */}
            <div style={{
              background: 'rgba(255,255,255,0.6)',
              borderRadius: 8,
              padding: '8px 10px',
              borderLeft: `2px solid ${
                healthSnapshot.overallStatus === 'low' ? '#10B981'
                  : healthSnapshot.overallStatus === 'moderate' ? '#F59E0B'
                  : '#EF4444'
              }`
            }}>
              <p style={{ 
                fontSize: 12, 
                color: '#4B5563', 
                margin: 0,
                lineHeight: 1.4
              }}>
                {healthSnapshot.recommendation}
              </p>
            </div>
          </>
        ) : (
          /* No Data State - Compact */
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <div style={{ 
              width: 40, 
              height: 40, 
              borderRadius: '50%', 
              background: '#F3F4F6', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              margin: '0 auto 10px',
              fontSize: 20
            }}>
              🔍
            </div>
            <p style={{ 
              fontSize: 13, 
              color: '#6B7280',
              margin: '0 0 12px 0',
              lineHeight: 1.4
            }}>
              Log symptoms and cycle data to see your health snapshot
            </p>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
              <button
                onClick={() => onNavigate('symptoms')}
                style={{
                  padding: '6px 12px',
                  background: '#6D5EF5',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Log Symptoms
              </button>
              <button
                onClick={() => onNavigate('cycle')}
                style={{
                  padding: '6px 12px',
                  background: '#F3F4F6',
                  color: '#4B5563',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Track Cycle
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 5. Quick Actions Section */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ 
          fontSize: 11, 
          fontWeight: 700, 
          color: '#8A8A9A', 
          letterSpacing: '0.1em', 
          marginBottom: 12, 
          textTransform: 'uppercase' 
        }}>
          QUICK ACTIONS
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Log Symptoms */}
          <div 
            onClick={() => onNavigate("symptoms")} 
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              padding: '14px 16px',
              background: '#fff',
              borderRadius: 14,
              boxShadow: '0 2px 12px rgba(109,94,245,0.06)',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            data-testid="action-symptoms"
          >
            <div style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: 'linear-gradient(135deg, #FEE2E2, #FECACA)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 22
            }}>
              📋
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#2F2F35' }}>Log Symptoms</div>
              <div style={{ fontSize: 12, color: '#8A8A9A' }}>Track how you're feeling today</div>
            </div>
            <div style={{ color: '#A78BFA', fontSize: 18 }}>→</div>
          </div>
          
          {/* Upload Hormone Report */}
          <div 
            onClick={() => onNavigate("hormones")} 
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              padding: '14px 16px',
              background: '#fff',
              borderRadius: 14,
              boxShadow: '0 2px 12px rgba(109,94,245,0.06)',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            data-testid="action-hormones"
          >
            <div style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: 'linear-gradient(135deg, #EDE9FE, #DDD6FE)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 22
            }}>
              🧬
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#2F2F35' }}>Upload Hormone Report</div>
              <div style={{ fontSize: 12, color: '#8A8A9A' }}>Analyze your lab results</div>
            </div>
            <div style={{ color: '#A78BFA', fontSize: 18 }}>→</div>
          </div>
          
          {/* Update Cycle */}
          <div 
            onClick={() => onNavigate("cycle")} 
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              padding: '14px 16px',
              background: '#fff',
              borderRadius: 14,
              boxShadow: '0 2px 12px rgba(109,94,245,0.06)',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            data-testid="action-cycle"
          >
            <div style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: 'linear-gradient(135deg, #D1FAE5, #A7F3D0)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 22
            }}>
              📅
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#2F2F35' }}>Update Cycle</div>
              <div style={{ fontSize: 12, color: '#8A8A9A' }}>Log period or update cycle length</div>
            </div>
            <div style={{ color: '#A78BFA', fontSize: 18 }}>→</div>
          </div>
        </div>
      </div>

      {/* 6. Export Health Report */}
      <div 
        onClick={() => onNavigate("profile")} 
        style={{
          background: 'linear-gradient(135deg, #F3F4F6, #E5E7EB)',
          borderRadius: 16,
          padding: '16px 18px',
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          cursor: 'pointer',
          border: '1px solid #E5E7EB'
        }}
        data-testid="export-health-report"
      >
        <div style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 20,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}>
          📄
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>Export Health Report</div>
          <div style={{ fontSize: 12, color: '#6B7280' }}>Download PDF summary of your data</div>
        </div>
        <div style={{ color: '#6B7280', fontSize: 16 }}>→</div>
      </div>

      {/* Weekly Insight Card */}
      <div className="insight-card" data-testid="insight-card">
        <span className="insight-pill">WEEKLY INSIGHT</span>
        <h3 className="insight-heading">Your Hormone Snapshot</h3>
        <p className="insight-body">{getInsightText()}</p>
        <button className="insight-link" onClick={() => onNavigate("hormones")} data-testid="view-report-link">
          View full report →
        </button>
      </div>

      {/* FEATURE 5: Check-In Modal */}
      {showCheckinModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(47,47,53,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }} data-testid="checkin-modal-overlay">
          <div style={{
            background: '#fff', borderRadius: 20, padding: 28,
            maxWidth: 360, width: '90%', maxHeight: '80vh', overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700 }}>How are you feeling today?</h3>
              <button onClick={() => setShowCheckinModal(false)} style={{
                background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#8A8A9A'
              }}>×</button>
            </div>
            {checkinItems.map((item) => (
              <div key={item.key} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 0', borderBottom: '1px solid #F0EEF8'
              }}>
                <span style={{ fontSize: 14 }}>{item.emoji} {item.label}</span>
                <div style={{ display: 'flex', gap: 4 }}>
                  {checkinLevels.map((level, idx) => (
                    <button key={level} onClick={() => setCheckinScores(p => ({ ...p, [item.key]: idx }))}
                      style={{
                        padding: '4px 10px', borderRadius: 20, fontSize: 12, border: 'none', cursor: 'pointer',
                        background: checkinScores[item.key] === idx ? 'linear-gradient(135deg, #A78BFA, #6D5EF5)' : '#EEEAF8',
                        color: checkinScores[item.key] === idx ? '#fff' : '#8A8A9A'
                      }}>
                      {level}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <button className="btn-primary" onClick={handleCheckinSave} style={{ marginTop: 20 }}
              disabled={Object.values(checkinScores).some(v => v === null)}
              data-testid="save-checkin-btn">
              Save Check-In
            </button>
          </div>
        </div>
      )}

      {/* Self-Check Card */}
      <div className="self-check-card" data-testid="self-check-card">
        <h3>Quick PCOS Self-Check</h3>
        {!quizStarted && !quizResult && (
          <>
            <p className="check-subtitle">5 questions, 1 minute</p>
            <button className="btn-primary" onClick={() => setQuizStarted(true)} data-testid="start-check-btn">Start Check</button>
          </>
        )}
        {quizStarted && !quizResult && (
          <>
            {quizQuestions.map((q) => (
              <div key={q.id} className="check-question" data-testid={`check-q-${q.id}`}>
                <span className="check-question-text">{q.text}</span>
                <div className="check-buttons">
                  <button className={`check-btn${quizAnswers[q.id] === "yes" ? " selected" : ""}`} onClick={() => setQuizAnswers((p) => ({ ...p, [q.id]: "yes" }))}>Yes</button>
                  <button className={`check-btn${quizAnswers[q.id] === "no" ? " selected" : ""}`} onClick={() => setQuizAnswers((p) => ({ ...p, [q.id]: "no" }))}>No</button>
                </div>
              </div>
            ))}
            <button className="btn-primary" disabled={Object.keys(quizAnswers).length < 5} onClick={handleQuizSubmit} data-testid="submit-check-btn" style={{ marginTop: 16 }}>
              See My Pattern
            </button>
          </>
        )}
        {quizResult && (
          <div className="check-result" data-testid="check-result">
            <p>{quizResult}</p>
            <p className="disclaimer">This is not a medical diagnosis. Please consult a qualified healthcare provider.</p>
            <button className="btn-ghost" onClick={() => { setQuizResult(null); setQuizAnswers({}); setQuizStarted(false); }} style={{ marginTop: 12 }} data-testid="retake-check-btn">
              Retake Self-Check
            </button>
          </div>
        )}
      </div>

      {todayLog && (
        <div className="today-log-card" data-testid="today-log">
          <h3>Today's Log</h3>
          {Object.entries(symptomLabels).map(([key, label]) => (
            <div key={key} className="log-row">
              <span>{label}</span>
              <span className="severity">{severityLabels[todayLog[key]] || "None"}</span>
            </div>
          ))}
          <button className="log-edit-link" onClick={() => onNavigate("symptoms")}>Edit</button>
        </div>
      )}
    </div>
  );
}
